/**
 * Debrief (08 §2.9): the stamped result, the score typed out line by line, then what the
 * mission earned - merit, requisition - and how the ranger came out of it. Brisk.
 */

import { mmss } from '../../hud/dom';
import { box, el, signed } from '../dom';
import { availability, multiplier } from '../text';
import { resultStamp, stamp } from '../widgets/stamp';
import type { Step } from './shell';
import { continueButton, holdInput, revealRows, scoreRow, stepShell } from './shell';

const STAMP_SETTLE_MS = 450;
const LINE_INTERVAL_MS = 120;
/** A finger still on the trigger when the mission ends must not skip the report. */
export const INPUT_GUARD_MS = 400;

function rule(): HTMLElement {
  return el('div', 'menu-score-rule');
}

export const debriefStep: Step = (env, next) => {
  const { result, aftermath, deployment } = env.input;
  const shell = stepShell('DEBRIEF');
  shell.content.append(box('menu-after-stampbox', stamp(resultStamp(result))));

  const lines = result.lines.map((line) =>
    scoreRow(line.label, signed(line.points), line.points < 0 ? 'menu-score-neg' : ''),
  );
  const tail = [
    scoreRow(`THREAT ${deployment.params.threat}`, multiplier(result.multiplier)),
    scoreRow('TOTAL', Math.round(result.total).toLocaleString('en-US'), 'menu-score-total'),
    scoreRow('TIME LEFT', mmss(result.secondsLeft)),
  ];
  const score = box('menu-score', ...lines, rule(), ...tail);
  const rows = [...lines, ...tail];

  if (aftermath !== null) {
    const gains = [
      scoreRow('MERIT', signed(aftermath.meritGained), 'menu-score-gain'),
      scoreRow('REQUISITION', `${signed(aftermath.rpGained)} RP`, 'menu-score-gain'),
    ];
    const state = availability(aftermath.ranger);
    const status = scoreRow('STATUS', state.chip, `menu-score-status menu-score-${state.tone}`);
    score.append(rule(), ...gains, status);
    rows.push(...gains, status);
    if (state.reason !== '') {
      const why = el('div', 'menu-score-row menu-score-why menu-prose', state.reason);
      score.append(why);
      rows.push(why);
    }
  }
  shell.content.append(score);

  const go = continueButton(next);
  shell.actions.append(go);
  env.timers.later(STAMP_SETTLE_MS, () => revealRows(env.timers, rows, LINE_INTERVAL_MS));
  holdInput(env.timers, go, INPUT_GUARD_MS);
  return { el: shell.el, focus: go };
};
